import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBrandGithub, TbExternalLink } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { type GitHubCIStatusPayload, type GitHubRollup, parseGitHubCIStatusPayload } from "./githubCIStatusSchema";
import { safeGitHubUrl } from "./githubRenderUtils";

interface GitHubCIStatusRendererProps {
  block: StreamingBlock;
}

const ROLLUP_PILL_CLASSES: Record<GitHubRollup, string> = {
  passing: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  failing: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  none: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

const ROLLUP_LABEL_KEYS: Record<GitHubRollup, string> = {
  passing: "github_ci_card.rollup_passing",
  failing: "github_ci_card.rollup_failing",
  pending: "github_ci_card.rollup_pending",
  none: "github_ci_card.rollup_none",
};

export const GitHubCIStatusRenderer = (props: GitHubCIStatusRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<GitHubCIStatusPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseGitHubCIStatusPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("github_ci_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // A ref with no checks is an answer, not a miss: the rollup pill
          // reports unconfigured CI, which green would misrepresent.
          <Show when={data().checks.length > 0 || data().rollup === "none"}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbBrandGithub class="size-4 shrink-0 text-primary-text" />
                <span class="truncate text-sm font-medium text-primary-text">
                  {rt.t("github_ci_card.title", { repo: data().repo, ref: data().ref })}
                </span>
                <span class={`ml-auto shrink-0 rounded px-1.5 py-0.5 text-xs ${ROLLUP_PILL_CLASSES[data().rollup]}`}>
                  {rt.t(ROLLUP_LABEL_KEYS[data().rollup])}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().checks}>
                  {(check) => (
                    <li class="flex items-baseline gap-2 px-3 py-2 text-sm">
                      <Show
                        when={safeGitHubUrl(check.url)}
                        fallback={<span class="truncate text-primary-text">{check.name}</span>}
                      >
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex min-w-0 items-baseline gap-1 truncate text-brand hover:underline"
                          >
                            {check.name}
                            <TbExternalLink class="size-3 shrink-0 self-center" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                      <Show when={check.app}>
                        {(app) => <span class="truncate text-xs text-muted-text">{app()}</span>}
                      </Show>
                      <span class="ml-auto shrink-0 text-xs text-muted-text">{check.conclusion ?? check.status}</span>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
